import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { ActivatedRoute, Router } from '@angular/router';
import { AssignedInboxComponent } from './assigned-inbox/assigned-inbox.component';
import { AssignedToMeComponent } from './assigned-to-me/assigned-to-me.component';
import { DelegatedToMeComponent } from './delegated-to-me/delegated-to-me.component';
import { CreatedByMeComponent } from './created-by-me/created-by-me.component';
import { AllParticipatedComponent } from './all-participated/all-participated.component';
import { CollaboratedTasksComponent } from './collaborated-tasks/collaborated-tasks.component';
import { TabComponent } from '../../../../ui-components/lib/tabs/tab/tab.component';
import { TabsComponent } from '../../../../ui-components/lib/tabs/tabs.component';
import { FilterDropdownService } from '../../../../ui-components/lib/filter-dropdown/filter-dropdown.service';
import { SearchService } from '../../../../ui-components/lib/search-field/search.service';
import { MyInboxService } from '../../../common/services/my-inbox/my-inbox.service';
import { MyInboxTabsInsight } from '../../../common/models/myinbox-insight.model';
import { EMPLOYEE_ROUTES } from '../../../common/constants/employee.routing.constants';
import { ContributedTasksComponent } from './contributed-tasks/contributed-tasks.component';
import { FEATURE_FLAGS } from '../../../common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../common/services/feature-flags/feature-flags.service';
import {
  injectSystemTabsLayout,
  injectFloatingLayout,
  provideSystemTabsLayout,
  injectFloatingPageClasses,
} from '../../../common/helpers/system-tabs-layout/system-tabs-layout';

@Component({
  selector: 'lib-my-inbox',
  imports: [
    NgTemplateOutlet,
    TabsComponent,
    TabComponent,
    TranslocoPipe,
    AssignedInboxComponent,
    AssignedToMeComponent,
    DelegatedToMeComponent,
    CreatedByMeComponent,
    AllParticipatedComponent,
    CollaboratedTasksComponent,
    ContributedTasksComponent,
  ],
  templateUrl: './my-inbox.component.html',
  styleUrl: './my-inbox.component.scss',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyInboxComponent {
  private readonly featureFlags = inject(FeatureFlagsService);
  protected readonly systemTabs = injectSystemTabsLayout();
  protected readonly floating = injectFloatingLayout();
  protected readonly systemTabsPageClasses = injectFloatingPageClasses();
  myInboxService = inject(MyInboxService);
  searchService = inject(SearchService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  filterService = inject(FilterDropdownService);
  insights = signal<MyInboxTabsInsight>({
    all_participated_user_count: 0,
    assigned_to_user_count: 0,
    created_by_user_count: 0,
    collaborated_user_count: 0,
    contributed_user_count: 0,
    delegated_tasks_count: 0,
  });
  actionSourceTabName = signal<string>('');
  inboxTabRoutingParam = EMPLOYEE_ROUTES.MY_INBOX.TABS;

  readonly delegatedToMeTabEnabled = computed(() =>
    this.featureFlags.getFeatureFlagValue(
      FEATURE_FLAGS.INBOX.delegated_to_me_tab
    )
  );

  readonly assignedToMeTabTag = computed(() => {
    const assigned = this.insights()?.assigned_to_user_count ?? 0;
    if (this.delegatedToMeTabEnabled()) {
      return assigned;
    }
    return assigned + (this.insights()?.delegated_tasks_count ?? 0);
  });

  readonly delegatedToMeTabTag = computed(
    () => this.insights()?.delegated_tasks_count ?? 0
  );

  constructor() {
    this.getTabsInsight();
  }

  getTabsInsight() {
    this.myInboxService.getTabsInsight().subscribe({
      next: res => {
        this.insights.set(res.data);
      },
    });
  }

  resetSearchAndFilterOnTabChange() {
    this.searchService.clearSearch();
    this.resetFilterOnTabChange();
  }

  resetFilterOnTabChange() {
    this.filterService.resetFiltersAndQueryParams(
      this.router,
      this.activatedRoute
    );
  }
}
